/**
 * CloseConfirmDialog displays a confirmation dialog when closing a game.
 *
 * Why: Closing a game affects all players, so we need user confirmation.
 * This uses FocusNavigationDialog for keyboard/gamepad navigation support.
 *
 * When to use: Rendered by GameControls, triggered when user clicks close
 * or presses Escape during gameplay.
 */
import * as React from "react"
import * as Dialog from "@radix-ui/react-dialog"
import { FocusNavigationDialog, useDialogNavigation, DialogNavigationItem } from "../../library/FocusNavigationDialog/FocusNavigationDialog"
import { ActionButton } from "../../library/ActionButton/ActionButton"
import { useControllerButtonHints } from "../../../hooks/useControllerButtonHints"
import { gameControlsTestIds } from "./gameControls.testIds"

export interface CloseConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  trigger: React.ReactElement
}

interface CloseDialogItem extends DialogNavigationItem {
  type: 'cancel' | 'confirm'
}

function CloseConfirmDialogContent({ onConfirm, onOpenChange }: { onConfirm: () => void; onOpenChange: (open: boolean) => void }) {
  const navigation = useDialogNavigation()
  const buttonHints = useControllerButtonHints()

  const handleCloseConfirm = React.useCallback(() => {
    onConfirm()
    onOpenChange(false)
  }, [onConfirm, onOpenChange])

  // Refs for dialog buttons
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null)
  const confirmCloseButtonRef = React.useRef<HTMLButtonElement>(null)

  // Register dialog buttons when dialog opens
  React.useEffect(() => {
    navigation.registerItem(0, cancelButtonRef.current)
    navigation.registerItem(1, confirmCloseButtonRef.current)
  }, [navigation])

  const cancelLabel = navigation.focusedIndex === 0 && buttonHints.cancel
    ? `Cancel (${buttonHints.cancel})`
    : 'Cancel'

  const confirmLabel = navigation.focusedIndex === 1 && buttonHints.select
    ? `Close Game (${buttonHints.select})`
    : 'Close Game'

  return (
    <>
      <p className="text-sm text-muted mb-4">
        Are you sure you want to close this game? This will end the game for all mates.
      </p>
      <div className="flex gap-2 justify-end">
        <Dialog.Close asChild>
          <ActionButton
            ref={cancelButtonRef}
            variant="outline"
            testId={gameControlsTestIds.cancelButton}
            isFocused={navigation.focusedIndex === 0}
          >
            {cancelLabel}
          </ActionButton>
        </Dialog.Close>
        <ActionButton
          ref={confirmCloseButtonRef}
          variant="default"
          onClick={handleCloseConfirm}
          testId={gameControlsTestIds.confirmCloseButton}
          isFocused={navigation.focusedIndex === 1}
          accentBackground
          glowOnFocus
        >
          {confirmLabel}
        </ActionButton>
      </div>
    </>
  )
}

export const CloseConfirmDialog = React.forwardRef<HTMLButtonElement, CloseConfirmDialogProps>(
  ({ open, onOpenChange, onConfirm, trigger }, ref) => {
    const navigationItems: CloseDialogItem[] = React.useMemo(() => [
      { id: 'cancel', type: 'cancel' as const },
      { id: 'confirm', type: 'confirm' as const },
    ], [])

    const handleNavigationSelect = React.useCallback((item: DialogNavigationItem) => {
      const dialogItem = item as CloseDialogItem
      if (dialogItem.type === 'cancel') {
        onOpenChange(false)
      } else {
        onConfirm()
        onOpenChange(false)
      }
    }, [onConfirm, onOpenChange])

    return (
      <FocusNavigationDialog
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        title="Close Game?"
        testId={gameControlsTestIds.closeConfirmDialog}
        navigationItems={navigationItems}
        onNavigationSelect={handleNavigationSelect}
        ref={ref}
      >
        <CloseConfirmDialogContent onConfirm={onConfirm} onOpenChange={onOpenChange} />
      </FocusNavigationDialog>
    )
  }
)
CloseConfirmDialog.displayName = "CloseConfirmDialog"
